import { useEffect, useRef, useCallback } from 'react';

// Import critical assets to get the correct CRA paths
import logoImage from '../Assets/prawnhublogowhite.png';
import prawnImage from '../Assets/prawn.png';

/**
 * Smart resource preloading hook for CRA/Vercel deployment
 * Only preloads assets that aren't already efficiently handled by CRA bundling
 */
export const useSmartPreload = () => {
  const preloadedRef = useRef(new Set());
  const imageCache = useRef(new Map());

  // Preload an image using JavaScript and cache it
  const preloadImage = useCallback((src, key) => {
    if (preloadedRef.current.has(key)) {
      return Promise.resolve(imageCache.current.get(key));
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        imageCache.current.set(key, img);
        preloadedRef.current.add(key);
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  // Preload critical resources that are always needed
  useEffect(() => {
    // Always preload logo since it's visible immediately in header/modal
    preloadImage(logoImage, 'logo').catch(err => 
      console.warn('Failed to preload logo:', err)
    );
    // Preload prawn.png immediately so it's ready when first entering vibe/kill mode
    preloadImage(prawnImage, 'prawn').catch(() => {});
  }, [preloadImage]);

  // Function to preload modal-specific images when needed
  const preloadModalAssets = useCallback((modalType) => {
    if (modalType === 'intro' || modalType === 'kill') {
      // Preload modal assets using dynamic imports and JavaScript Image loading
      const modalAssets = [
        { import: () => import('../Assets/prawn.png'), key: 'prawn' },
        { import: () => import('../Assets/fried_prawn.png'), key: 'fried-prawn' },
        { import: () => import('../Assets/speaker-icon.png'), key: 'speaker' },
        { import: () => import('../Assets/book.png'), key: 'book' },
      ];

      modalAssets.forEach(({ import: importFn, key }) => {
        if (!preloadedRef.current.has(key)) {
          importFn().then(module => {
            preloadImage(module.default, key);
          }).catch(err => {
            console.warn(`Failed to preload modal asset ${key}:`, err);
          });
        }
      });
    }
  }, [preloadImage]);

  // Expose the preloaded image cache for direct usage
  const getPreloadedImage = useCallback((key) => {
    return imageCache.current.get(key);
  }, []);

  // Preload HD Prawn Mode assets (lazy — only when entering vibe/kill simulator)
  const preloadHdAssets = useCallback(() => {
    const hdAssets = [
      { import: () => import('../Assets/prawn-8k.png'), key: 'prawn-8k' },
      { import: () => import('../Assets/eye-icon.png'), key: 'eye-icon' },
      { import: () => import('../Assets/desktopbackground-8k.jpg'), key: 'desktop-bg-8k' },
      { import: () => import('../Assets/mobilebackground-8k.jpg'), key: 'mobile-bg-8k' },
    ];
    hdAssets.forEach(({ import: importFn, key }) => {
      if (!preloadedRef.current.has(key)) {
        importFn().then(module => {
          preloadImage(module.default, key);
        }).catch(() => {});
      }
    });
  }, [preloadImage]);

  return { 
    preloadModalAssets, 
    preloadHdAssets,
    getPreloadedImage,
    isPreloaded: (key) => preloadedRef.current.has(key)
  };
};

/**
 * Preload specific invasive species assets for kill simulator
 */
export const useInvasiveAssetsPreload = () => {
  const preloadedInvasives = useRef(false);

  const preloadInvasives = useCallback(() => {
    if (preloadedInvasives.current) return;

    const invasiveAssets = [
      'anglerfish.png',
      'blobfish.png',
      'blobfish2.png',
      'blobfish3.png',
      'burger.png',
      'creepytoy.png',
      'deepseacreature.png',
      'energydrink.png',
      'monkfish.png',
      'plasticbag.png',
      'severedhand.png',
      'sixpackring.png',
      'smilingsalamander.png',
      'straw.png',
      'syringe.png',
      'toaster.png',
      'vape.png',
      'waterbottle.png',
      'wrapper.png',
    ];

    // Use JavaScript Image loading instead of dynamic imports for faster preloading
    invasiveAssets.forEach(assetName => {
      import(`../Assets/Invasives/${assetName}`)
        .then(module => {
          const img = new Image();
          img.src = module.default;
        })
        .catch(err => {
          console.warn(`Failed to preload invasive asset: ${assetName}`, err);
        });
    });

    preloadedInvasives.current = true;
  }, []);

  return { preloadInvasives };
};
